// SPDX-License-Identifier: GPL-3.0-or-later

import { el } from './dom';

const REPO_URL = 'https://github.com/JeanHaiz/applify';

function h2(text: string): HTMLElement {
  return el(
    'h3',
    {
      style:
        'font-family: var(--font-serif); font-style: italic; font-size: 1.5rem; color: var(--text); margin-top: 1.25rem;',
    },
    text,
  );
}

function p(...children: (string | Node)[]): HTMLElement {
  return el(
    'p',
    { style: 'color: var(--text-secondary); line-height: 1.65;' },
    ...children,
  );
}

function link(href: string, text: string): HTMLElement {
  return el('a', { href, target: '_blank', rel: 'noreferrer' }, text);
}

export function renderPrivacy(): HTMLElement {
  return el(
    'section',
    {
      style:
        'max-width: 44rem; margin: 0 auto; display: flex; flex-direction: column; gap: 0.5rem;',
    },
    el(
      'h2',
      {
        style:
          'font-family: var(--font-serif); font-style: italic; font-size: 2.6rem; color: var(--text); line-height: 1.1;',
      },
      'Privacy policy',
    ),
    el(
      'div',
      {
        style:
          'font-size: 0.72rem; color: var(--text-muted); letter-spacing: 0.04em;',
      },
      'Effective 2026-05-19 · Last updated 2026-05-19',
    ),
    p(
      'This policy applies to the ',
      el('strong', {}, 'canonical Applify deployment'),
      ' served at ',
      el('code', { style: 'color: var(--text);' }, 'applify.jean-haizmann.workers.dev'),
      '. Anyone hosting their own deployment is its own data controller and may have different practices.',
    ),

    h2('1. Who we are'),
    p(
      'Applify is a personal hobby project maintained by Jean Haizmann. There is no company, no investors, no analytics team behind it.',
    ),
    p('Contact: ', link(REPO_URL, 'github.com/JeanHaiz/applify'), '.'),

    h2('2. Data the app handles'),
    p('On your device only:'),
    el(
      'ul',
      { style: 'color: var(--text-secondary); line-height: 1.7; padding-left: 1.5rem;' },
      el('li', {}, 'Job postings you save (title, company, URL, apply type, full description, capture date)'),
      el('li', {}, 'Your profile (CV text + freeform notes + job preferences)'),
      el('li', {}, 'Generated documents (CV / cover letter / interview prep, .tex + PDF)'),
      el('li', {}, 'Status, notes, and configuration'),
    ),

    h2('3. Where data lives'),
    p(
      'Everything sits in a folder ',
      el('em', {}, 'on your device'),
      ', accessed via the File System Access API. There is no Applify-operated server, no cloud database, no telemetry endpoint. The deployment is static HTML / JS / CSS / WASM served by Cloudflare.',
    ),

    h2('4. Third parties (opt-in)'),
    p('Applify is designed so most features work without sending data anywhere. The opt-in third parties:'),

    el('strong', { style: 'color: var(--text); margin-top: 0.5rem; display: block;' }, '4.1 Cloudflare'),
    p(
      'Hosts the static site. Standard request metadata (IP, user agent, path) is logged per Cloudflare\'s ',
      link('https://www.cloudflare.com/privacypolicy/', 'privacy policy'),
      '. The maintainer only sees aggregate dashboard stats.',
    ),

    el('strong', { style: 'color: var(--text); margin-top: 0.5rem; display: block;' }, '4.2 GitHub'),
    p(
      'Hosts the source. See ',
      link('https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement', 'GitHub\'s privacy statement'),
      '.',
    ),

    el('strong', { style: 'color: var(--text); margin-top: 0.5rem; display: block;' }, '4.3 Injinary Wallet'),
    p(
      'If you enable AI features, prompts pass through the ',
      link('https://chromewebstore.google.com/detail/injinary-wallet/emnpfdhpjmgbdgmbpcbloncillceljgp', 'Injinary Wallet extension'),
      ', which sees both your API key and the prompt content it relays.',
    ),

    el('strong', { style: 'color: var(--text); margin-top: 0.5rem; display: block;' }, '4.4 Your chosen LLM provider'),
    p(
      'AI prompts — your profile text plus the relevant job description — go to the provider you select in Settings → AI. Applify defaults to Anthropic; OpenAI is also requested by the wallet and used if you pick one of its models. Whichever you pick, the terms and data-handling rules are between you and that provider:',
    ),
    el(
      'ul',
      { style: 'color: var(--text-secondary); line-height: 1.7; padding-left: 1.5rem;' },
      el(
        'li',
        {},
        'Anthropic — see their ',
        link('https://www.anthropic.com/legal/commercial-terms', 'commercial terms'),
        '. API submissions are not used to train their models.',
      ),
      el(
        'li',
        {},
        'OpenAI — see their ',
        link('https://openai.com/policies/api-data-usage-policies/', 'API data usage policies'),
        '. API submissions are not used to train their models by default.',
      ),
    ),

    el('strong', { style: 'color: var(--text); margin-top: 0.5rem; display: block;' }, '4.5 Typst default fonts (jsDelivr)'),
    p(
      'On the first PDF compile, the in-browser Typst engine fetches its default text-font bundle from jsDelivr (',
      el('code', { style: 'color: var(--text);' }, 'cdn.jsdelivr.net'),
      '). These requests reveal which fonts the engine wants, not your document contents. The compiler WASM itself ships with the Applify deploy — no other upstream calls happen during compile. Self-hosting these fonts is a planned follow-up.',
    ),

    h2('5. What the app does not do'),
    el(
      'ul',
      { style: 'color: var(--text-secondary); line-height: 1.7; padding-left: 1.5rem;' },
      el('li', {}, 'No analytics, tracking pixels, fingerprinting.'),
      el('li', {}, 'No advertising.'),
      el('li', {}, 'No cookies set by the app.'),
      el('li', {}, 'No account system, no email collection, nothing to log out of.'),
      el('li', {}, 'No background sync, telemetry, or error reporting to the maintainer.'),
      el('li', {}, 'No data leaves your device unless you trigger an AI action.'),
    ),

    h2('6. Browser storage'),
    p(
      'IndexedDB stores the directory handle and a manifest cache. localStorage stores a few UI preferences. BroadcastChannel coordinates multiple tabs but persists nothing. Clearing site data in your browser deletes all of the above; files on disk are untouched.',
    ),

    h2('7. Children'),
    p('Applify is not directed at children under 13.'),

    h2('8. Changes to this policy'),
    p(
      'Any change is recorded in ',
      el('code', { style: 'color: var(--text);' }, 'CHANGELOG.md'),
      ' and the Effective date above is updated. Continued use after a change constitutes acceptance.',
    ),

    h2('9. Your rights'),
    p(
      'Applify holds none of your data on any server. To exercise GDPR/CCPA-style rights:',
    ),
    el(
      'ul',
      { style: 'color: var(--text-secondary); line-height: 1.7; padding-left: 1.5rem;' },
      el(
        'li',
        {},
        el('strong', { style: 'color: var(--text);' }, 'Access / portability:'),
        ' open your folder, or click ',
        el('strong', {}, 'Settings → Export everything as ZIP'),
        '.',
      ),
      el(
        'li',
        {},
        el('strong', { style: 'color: var(--text);' }, 'Deletion:'),
        ' delete the root folder and clear browser site data for the Applify origin.',
      ),
    ),

    h2('10. Forks'),
    p(
      'If you\'re using a fork at a non-canonical URL, the above is informational; that fork\'s operator is its own data controller. Check that deployment\'s notice.',
    ),

    el(
      'p',
      { style: 'font-size: 0.72rem; color: var(--text-muted); margin-top: 1.5rem;' },
      'The Markdown version of this policy lives at ',
      link(`${REPO_URL}/blob/main/PRIVACY.md`, 'PRIVACY.md'),
      '.',
    ),
  );
}
